import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/api/query/querykeys";
import Dialog from "@/components/common/Dialog";
import Loader from "@/components/common/Loader";
import ProjectStatusEditBlockedDialog from "@/components/project/ProjectStatusEditBlockedDialog";
import { errorConfig } from "@/lib/toastError";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import {
  ProjectStatusEnumValue,
  TaskStatusEnumValue,
  TaskDependenciesEnumValue,
} from "@backend/src/schemas/enums";
import useRemoveTaskDependenciesMutation from "@/api/mutation/useTaskRemoveDependencies";

// Domain Subcomponents & Hooks
import { TaskHeader } from "./components/TaskHeader";
import { TaskStatusAndProgress } from "./components/TaskStatusAndProgress";
import { TaskPropertiesGrid } from "./components/TaskPropertiesGrid";
import { TaskAssigneeSection } from "./components/TaskAssigneeSection";
import { TaskDescriptionEditor } from "./components/TaskDescriptionEditor";
import { TaskNotesSection } from "./components/TaskNotesSection";
import { TaskSubtasksList } from "./components/TaskSubtasksList";
import { TaskAttachmentsSection } from "./components/TaskAttachmentsSection";
import { TaskDependenciesSection } from "./components/TaskDependenciesSection";
import { useTaskPermissions } from "./hooks/useTaskPermissions";
import type { TaskDetailModalProps, TaskNoteItem, ApiErrorLike } from "./types";

// Existing mutations & queries
import useTaskQuery from "@/api/query/useTaskQuery";
import useAllTaskQuery from "@/api/query/useAllTaskQuery";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import useCreateTaskMutation, { Task, TaskNotes } from "@/api/mutation/useTaskCreateMutation";
import useUpdateTaskMutation from "@/api/mutation/useTaskUpdateMutation";
import useRemoveTaskMutation from "@/api/mutation/useTaskRemove";
import useTaskAddMembersMutation from "@/api/mutation/useTaskAddMember";
import useRemoveTaskMemberMutation from "@/api/mutation/useTaskRemoveMember";
import useUpdateTaskMemberMutation from "@/api/mutation/useAssignTaskMemeberUpdateMutation";
import useUpdateTaskParentMutation from "@/api/mutation/useTaskParentUpdateMutation";
import useTaskAddUpdateMilestoneMutation from "@/api/mutation/useTaskAddUpdateMilestone";
import useAddNoteMutation from "@/api/mutation/useAddTaskNotesMutation";
import useUpdateNoteMutation from "@/api/mutation/useUpdateTaskNotesMutation";
import useRemoveNoteMutation from "@/api/mutation/useRemoveNotesMutation";
import useTaskAttechmentAddMutation from "@/api/mutation/useTaskAttechmentAddMutation";
import useSharePointAttechmentAddMutation from "@/api/mutation/useSharePointAttechmentMutaion";
import useTaskStatusUpdateMutation from "@/api/mutation/useTaskStatusUpdateMutation";
import { useTaskLinkAttachmentAddMutation } from "@/api/mutation/useTaskLinkAttachmentAddMutation";
import { msalClientId, appUrl, googleApiKey, googleCredentialsClientId } from "@/Environment";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import useOrganisationDetailsQuery, { type UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
import { getCurrentUserOrganisation } from "@/utils/roleUtils";
import { mapOrgDayToNumber } from "@/components/common/CommonDatePicker";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";
import type { AssignedUsers } from "@/api/query/useProjectQuery";
import TrashCan from "@/assets/svg/TrashCan.svg";
import { Button } from "@/components/ui/button";
import { ListTree, Paperclip, FileText, Link2, MessageSquare, History } from "lucide-react";
import TaskComment from "../taskComment";
import TaskHistory from "../taskHistory";

type TaskStatus = (typeof TaskStatusEnumValue)[keyof typeof TaskStatusEnumValue];

export const TaskDetailModal: React.FC<TaskDetailModalProps> = (props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tempIdRef = useRef("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [taskId, setTaskId] = useState<string>(props.taskId ?? "");

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [taskId]);
  const [taskName, setTaskName] = useState("");
  const [isEditingName, setIsEditingName] = useState(!props.taskId);
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [duration, setDuration] = useState<number>(1);
  const [isMilestone, setIsMilestone] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<"subtasks" | "attachments" | "notes" | "dependencies" | "activity" | "history">("subtasks");

  // Queries
  const taskQuery = useTaskQuery(taskId, !!taskId);
  const allTasksQuery = useAllTaskQuery(props.projectId, !!props.projectId);
  const projectDetailQuery = useProjectDetail(props.projectId, !!props.projectId);

  const { data: userResponse } = useCurrentUserQuery();
  const user = userResponse?.data?.data;
  const currentOrg = getCurrentUserOrganisation(user);
  const orgId = currentOrg?.organisationId || localStorage.getItem("organisation-id") || "";
  const { data: orgDetailResponse } = useOrganisationDetailsQuery(orgId, Boolean(orgId));
  const liveOrg = orgDetailResponse?.data?.data;

  const { orgNonWorkingDays, orgHolidays } = useMemo(() => {
    const fallbackOrg = currentOrg?.organisation;
    const nonWorkingDaysList: OrgListOfNonWorkingDaysEnum[] =
      liveOrg?.nonWorkingDays ?? fallbackOrg?.nonWorkingDays ?? [];
    const holidaysList = liveOrg?.orgHolidays ?? fallbackOrg?.orgHolidays ?? [];

    const nonWorkingDays =
      nonWorkingDaysList.length > 0
        ? nonWorkingDaysList.map(mapOrgDayToNumber)
        : [0, 6];

    const holidays = holidaysList
      .map((h) => new Date(h.holidayStartDate))
      .filter((d) => !isNaN(d.getTime()));

    return { orgNonWorkingDays: nonWorkingDays, orgHolidays: holidays };
  }, [liveOrg, currentOrg]);

  const [task, setTask] = useState<Task | undefined>(
    taskQuery.data?.data?.data && taskId ? taskQuery.data?.data?.data : undefined,
  );

  useEffect(() => {
    if (taskQuery.status === "success" && taskQuery.data?.data?.data) {
      const fetchedTask = taskQuery.data.data.data;
      setTask(fetchedTask);
      setTaskName(fetchedTask.taskName || "");
      setDescription(fetchedTask.taskDescription || "");
      if (fetchedTask.startDate) setStartDate(new Date(fetchedTask.startDate));
      if (fetchedTask.duration) setDuration(Number(fetchedTask.duration));
      setIsMilestone(Boolean(fetchedTask.milestoneIndicator));
    }
  }, [taskQuery.data?.data?.data, taskQuery.status]);

  // Inherit start date when creating a subtask or new task
  useEffect(() => {
    if (!taskId) {
      const parentTaskId = task?.parentTaskId ?? props.createSubtask;
      const parentTask = allTasksQuery.data?.data?.data?.find(
        (t) => t.taskId === parentTaskId,
      );

      const initialStartDate = props.initialValues?.startDate;
      const project = projectDetailQuery?.data?.data?.data;
      const projectStartDate = project?.startDate;

      let date = new Date();
      if (initialStartDate) {
        date = new Date(initialStartDate);
      } else if (projectStartDate) {
        const pDate = new Date(projectStartDate);
        const today = new Date();
        date = pDate > today ? pDate : today;
      }

      if (parentTask?.startDate) {
        date = new Date(parentTask.startDate);
      }

      setStartDate(date);
    }
  }, [
    taskId,
    task?.parentTaskId,
    props.createSubtask,
    props.initialValues?.startDate,
    projectDetailQuery?.data?.data?.data,
    allTasksQuery.data?.data?.data,
  ]);

  // Project Edit Blocked check
  const projectStatus = projectDetailQuery.data?.data?.data?.status;
  const isProjectEditBlocked =
    projectStatus === ProjectStatusEnumValue.CANCELLED ||
    projectStatus === ProjectStatusEnumValue.ON_HOLD ||
    projectStatus === ProjectStatusEnumValue.COMPLETED ||
    projectStatus === ProjectStatusEnumValue.CLOSED;

  const [isProjectEditBlockedDialogOpen, setIsProjectEditBlockedDialogOpen] =
    useState(false);

  useEffect(() => {
    if (isProjectEditBlocked) {
      setIsProjectEditBlockedDialogOpen(true);
    }
  }, [isProjectEditBlocked]);

  // Role permissions
  const { allowed, currentUser } = useTaskPermissions({
    taskId,
    project: projectDetailQuery.data?.data?.data,
    task,
  });

  const refetch = useCallback(() => {
    const activeId = taskId || tempIdRef.current;
    if (activeId) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, activeId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, props.projectId] });
    }
  }, [queryClient, taskId, props.projectId]);

  // Mutations
  const taskCreateMutation = useCreateTaskMutation(
    props.projectId,
    props.createSubtask ? props.createSubtask : taskId ? taskId : "",
  );
  const taskUpdateMutation = useUpdateTaskMutation(taskId || tempIdRef.current);
  const taskRemoveMutation = useRemoveTaskMutation();
  const taskStatusMutation = useTaskStatusUpdateMutation(taskId || tempIdRef.current);
  const taskAddMembersMutation = useTaskAddMembersMutation(taskId || tempIdRef.current);
  const taskRemoveMembersMutation = useRemoveTaskMemberMutation();
  const taskUpdateMembersMutation = useUpdateTaskMemberMutation();
  const milestoneMutation = useTaskAddUpdateMilestoneMutation(taskId || tempIdRef.current);
  const updateTaskParentMutation = useUpdateTaskParentMutation();
  const addNoteMutation = useAddNoteMutation(taskId || tempIdRef.current);
  const updateNoteMutation = useUpdateNoteMutation();
  const removeNoteMutation = useRemoveNoteMutation();
  const attachmentAddMutation = useTaskAttechmentAddMutation(taskId || tempIdRef.current);
  const sharePointAttechmentAddMutation = useSharePointAttechmentAddMutation(taskId || tempIdRef.current);
  const { mutate: addLinkMutation, isPending: isLinkUploading } =
    useTaskLinkAttachmentAddMutation(taskId || tempIdRef.current);
  const removeTaskDependenciesMutation = useRemoveTaskDependenciesMutation();
  const [invalidDateConflict, setInvalidDateConflict] = useState(false);
  const [conflictErrorMessage, setConflictErrorMessage] = useState("");
  const [pendingDateUpdate, setPendingDateUpdate] = useState<Date | null>(null);

  // Lazy task creator when user interacts with uncreated task
  const ensureTaskCreated = async (overrides: Partial<Task> = {}): Promise<string> => {
    if (taskId) return taskId;
    if (tempIdRef.current) return tempIdRef.current;

    const nameToUse = (overrides.taskName ?? taskName)?.trim();
    if (!nameToUse) {
      toast.error(t("task_name_required") || t("Task name is required"), errorConfig);
      return "";
    }

    const effectiveStart = overrides.startDate ?? startDate;
    const dateObj = effectiveStart instanceof Date ? effectiveStart : new Date(effectiveStart);
    const utcStartDate = new Date(
      Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 0, 0, 0)
    );

    const payload = {
      taskName: nameToUse,
      taskDescription: overrides.taskDescription ?? description,
      startDate: utcStartDate,
      duration: overrides.duration ?? duration,
      completionPecentage: overrides.completionPecentage ? Number(overrides.completionPecentage) : undefined,
      kanbanColumnId: overrides.kanbanColumnId ?? undefined,
    };

    const res = await taskCreateMutation.mutateAsync(payload);
    const createdData = res.data.data;
    const newId = createdData.taskId;
    tempIdRef.current = newId;
    setTaskId(newId);
    setTask((prev) => (prev ? { ...prev, ...createdData } : createdData));
    refetch();
    return newId;
  };

  // Handlers
  const handleTaskNameSubmit = async () => {
    if (!taskName.trim()) return;
    setIsEditingName(false);
    if (!taskId) {
      await ensureTaskCreated({ taskName: taskName.trim() });
    } else {
      taskUpdateMutation.mutate({ taskName: taskName.trim() }, { onSuccess: refetch });
    }
  };

  const handleDescriptionSubmit = async (val: string) => {
    setDescription(val);
    if (!taskId) {
      await ensureTaskCreated({ taskDescription: val });
    } else {
      taskUpdateMutation.mutate({ taskDescription: val }, { onSuccess: refetch });
    }
  };

  const deletePredecessorDependency = () => {
    const taskDependenciesId = taskQuery?.data?.data?.data?.dependencies?.find(
      (item) =>
        item.dependentTaskId === (task?.taskId || taskId) &&
        item.dependentType === TaskDependenciesEnumValue.PREDECESSORS,
    )?.taskDependenciesId;

    if (taskDependenciesId) {
      removeTaskDependenciesMutation.mutate(taskDependenciesId, {
        onSuccess: (data) => {
          refetch();
          if (pendingDateUpdate) {
            const utcDate = new Date(
              Date.UTC(
                pendingDateUpdate.getFullYear(),
                pendingDateUpdate.getMonth(),
                pendingDateUpdate.getDate(),
                0,
                0,
                0,
              ),
            );
            taskUpdateMutation.mutate(
              { startDate: utcDate },
              {
                onSuccess: () => {
                  refetch();
                },
              },
            );
          }
          toast.success(data?.data?.message || t("dependency_removed"));
          setInvalidDateConflict(false);
          setPendingDateUpdate(null);
        },
        onError: (err: ApiErrorLike) => {
          toast.error(
            err?.response?.data?.message === "Unauthorized"
              ? t("You are not authorized to edit tasks which are not assigned to you")
              : errorKeyFormate(err?.response?.data?.message || err?.message || ""),
            errorConfig,
          );
          setInvalidDateConflict(false);
          setPendingDateUpdate(null);
        },
      });
    } else {
      setInvalidDateConflict(false);
      setPendingDateUpdate(null);
    }
  };

  const handleStartDateChange = async (date: Date) => {
    setStartDate(date);
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
    if (!taskId) {
      await ensureTaskCreated({ startDate: utcDate });
    } else {
      taskUpdateMutation.mutate(
        { startDate: utcDate },
        {
          onSuccess: (res) => {
            if (res?.data?.data) {
              setTask((prev) => (prev ? { ...prev, ...res.data.data } : res.data.data));
            }
            refetch();
          },
          onError: (err: ApiErrorLike) => {
            const msg = err?.response?.data?.message || err?.message || "";
            if (
              msg.includes("Would you like to remove the dependency?") ||
              msg.includes("Would you like to remove these dependencies?")
            ) {
              setConflictErrorMessage(msg);
              setPendingDateUpdate(date);
              setInvalidDateConflict(true);
            } else {
              toast.error(errorKeyFormate(msg), errorConfig);
            }
          },
        }
      );
    }
  };

  const handleDurationChange = async (newDuration: number) => {
    setDuration(newDuration);
    if (!taskId) {
      await ensureTaskCreated({ duration: newDuration });
    } else {
      taskUpdateMutation.mutate(
        { duration: newDuration },
        {
          onSuccess: (res) => {
            if (res?.data?.data) {
              setTask((prev) => (prev ? { ...prev, ...res.data.data } : res.data.data));
            }
            refetch();
          },
          onError: (err: ApiErrorLike) => {
            const msg = err?.response?.data?.message || err?.message || "";
            if (
              msg.includes("Would you like to remove the dependency?") ||
              msg.includes("Would you like to remove these dependencies?")
            ) {
              setConflictErrorMessage(msg);
              setInvalidDateConflict(true);
            } else {
              toast.error(errorKeyFormate(msg), errorConfig);
            }
          },
        }
      );
    }
  };

  const handleEndDateChange = async (newEndDate: Date) => {
    // Calculate working days duration from startDate to newEndDate
    const start = new Date(startDate);
    const end = new Date(newEndDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (end < start) return;

    let count = 0;
    const curr = new Date(start);
    while (curr <= end) {
      const isNonWorkingDay = orgNonWorkingDays.includes(curr.getDay());
      const isHoliday = orgHolidays.some(
        (h) =>
          h.getFullYear() === curr.getFullYear() &&
          h.getMonth() === curr.getMonth() &&
          h.getDate() === curr.getDate()
      );
      if (!isNonWorkingDay && !isHoliday) {
        count++;
      }
      curr.setDate(curr.getDate() + 1);
    }
    const newDuration = Math.max(1, count);
    setDuration(newDuration);

    if (!taskId) {
      await ensureTaskCreated({ duration: newDuration });
    } else {
      taskUpdateMutation.mutate(
        { duration: newDuration },
        {
          onSuccess: (res) => {
            if (res?.data?.data) {
              setTask((prev) => (prev ? { ...prev, ...res.data.data } : res.data.data));
            }
            refetch();
          },
          onError: (err: ApiErrorLike) => {
            const msg = err?.response?.data?.message || err?.message || "";
            if (
              msg.includes("Would you like to remove the dependency?") ||
              msg.includes("Would you like to remove these dependencies?")
            ) {
              setConflictErrorMessage(msg);
              setInvalidDateConflict(true);
            } else {
              toast.error(errorKeyFormate(msg), errorConfig);
            }
          },
        }
      );
    }
  };

  const handleMilestoneToggle = async (checked: boolean) => {
    setIsMilestone(checked);
    const activeId = await ensureTaskCreated({ milestoneIndicator: checked });
    if (!activeId) return;
    milestoneMutation.mutate(
      { milestoneIndicator: checked, taskId: activeId },
      { onSuccess: refetch },
    );
  };

  const handleStatusChange = async (status: TaskStatus) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    taskStatusMutation.mutate(
      { taskId: activeId, status },
      {
        onSuccess: () => {
          refetch();
          setTask((prev) => (prev ? { ...prev, status } : prev));
        },
        onError: (err: ApiErrorLike) => {
          const msg = err?.response?.data?.message || err?.message || "";
          if (
            msg.includes("Would you like to remove the dependency?") ||
            msg.includes("Would you like to remove these dependencies?")
          ) {
            setConflictErrorMessage(msg);
            setInvalidDateConflict(true);
          } else {
            toast.error(errorKeyFormate(msg), errorConfig);
          }
        },
      },
    );
  };

  const handleProgressCommit = async (percentage: number) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    taskUpdateMutation.mutate(
      { completionPecentage: percentage },
      {
        onSuccess: () => {
          refetch();
          setTask((prev) => (prev ? { ...prev, completionPecentage: String(percentage) } : prev));
        },
      },
    );
  };

  const handleToggleMember = async (memberData: UserOrganisationType, workingHours = 8) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    const existing = task?.assignedUsers?.find((u) => u.user.userId === memberData.user.userId);
    if (existing) {
      taskRemoveMembersMutation.mutate(existing.taskAssignUsersId, {
        onSuccess: () => {
          setTask((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              assignedUsers: (prev.assignedUsers || []).filter(
                (u) => u.taskAssignUsersId !== existing.taskAssignUsersId,
              ),
            };
          });
          refetch();
          projectDetailQuery.refetch();
        },
      });
    } else {
      taskAddMembersMutation.mutate(
        { assginedToUserId: memberData.user.userId, workingHours, taskId: activeId },
        {
          onSuccess: (res) => {
            if (res?.data?.data) {
              const resData = res.data.data;
              const newAssignment: Task["assignedUsers"][number] = {
                assginedToUserId: resData.assginedToUserId,
                taskAssignUsersId: resData.taskAssignUsersId,
                user: memberData.user,
                workingHours,
              };
              setTask((prev) => {
                if (!prev) return prev;
                const currentUsers = prev.assignedUsers || [];
                return {
                  ...prev,
                  assignedUsers: [...currentUsers, newAssignment],
                };
              });
            }
            refetch();
            projectDetailQuery.refetch();
          },
        },
      );
    }
  };

  const handleUpdateMemberAssignment = async (
    memberData: UserOrganisationType,
    workingHours: number,
  ) => {
    const activeId = taskId || tempIdRef.current;
    if (!activeId) return;

    const assignId =
      task?.assignedUsers?.find((t) => t.user.userId === memberData.user.userId)
        ?.taskAssignUsersId ?? "";

    if (!assignId) return;

    taskUpdateMembersMutation.mutate(
      { assginedToUserId: assignId, workingHours },
      {
        onSuccess: () => {
          setTask((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              assignedUsers: (prev.assignedUsers || []).map((u) =>
                u.taskAssignUsersId === assignId
                  ? { ...u, workingHours }
                  : u,
              ),
            };
          });
          refetch();
          projectDetailQuery.refetch();
        },
        onError: (err: ApiErrorLike) => {
          toast.error(errorKeyFormate(err?.response?.data?.message || err.message || ""), errorConfig);
        },
      },
    );
  };

  const handleAddNote = async (noteText: string) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    addNoteMutation.mutate(
      { note: noteText, tempId: activeId },
      {
        onSuccess: (data) => {
          refetch();
          const activeId = taskId || tempIdRef.current;
          if (activeId) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, activeId] });
          }
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, props.projectId] });
          const newNote: TaskNotes = {
            noteId: data.data.data.noteId,
            note: data.data.data.note,
            taskId: data.data.data.taskId,
            createdByUserId: data.data.data.createdByUserId,
            updatedByUserId: data.data.data.updatedByUserId,
            createdAt: data.data.data.createdAt,
            updatedAt: data.data.data.updatedAt || "",
            createdByUser: currentUser as unknown as UserOrganisationType["user"],
            updatedByUser: currentUser as unknown as UserOrganisationType["user"],
          };
          setTask((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              taskNotes: [...(prev.taskNotes || []), newNote],
            };
          });
        },
      },
    );
  };

  const handleUpdateNote = (noteId: string, noteText: string) => {
    updateNoteMutation.mutate(
      { noteId, note: noteText },
      {
        onSuccess: (data) => {
          refetch();
          const activeId = taskId || tempIdRef.current;
          if (activeId) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, activeId] });
          }
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, props.projectId] });
          setTask((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              taskNotes: (prev.taskNotes || []).map((n) =>
                n.noteId === noteId
                  ? {
                    ...n,
                    note: data.data.data.note,
                    updatedAt: data.data.data.updatedAt || n.updatedAt,
                  }
                  : n,
              ),
            };
          });
        },
      },
    );
  };

  const handleDeleteNote = (noteId: string) => {
    removeNoteMutation.mutate(noteId, {
      onSuccess: () => {
        refetch();
        const activeId = taskId || tempIdRef.current;
        if (activeId) {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, activeId] });
        }
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, props.projectId] });
        setTask((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            taskNotes: (prev.taskNotes || []).filter((n) => n.noteId !== noteId),
          };
        });
      },
    });
  };

  const handleAddSubtask = async (subtaskName: string) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    taskCreateMutation.mutate(
      {
        taskName: subtaskName,
        taskDescription: "",
        startDate: task?.startDate ? new Date(task.startDate) : new Date(),
        duration: 1,
        tempId: activeId,
      },
      {
        onSuccess: () => {
          refetch();
          allTasksQuery.refetch();
        },
      },
    );
  };

  const handleAttachExistingTask = async (subtaskToAttach: Task) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    updateTaskParentMutation.mutate(
      {
        parentTaskId: activeId,
        id: subtaskToAttach.taskId,
      },
      {
        onSuccess: () => {
          refetch();
          allTasksQuery.refetch();
        },
        onError: (err: ApiErrorLike) => {
          toast.error(errorKeyFormate(err?.response?.data?.message || err.message || ""), errorConfig);
        },
      },
    );
  };

  const handleUploadFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    if (fileList.length === 0) return;
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    const formData = new FormData();
    fileList.forEach((f) => {
      formData.append("taskAttachment", f);
      formData.append("files", f);
    });
    formData.append("taskId", activeId);
    attachmentAddMutation.mutate(
      { FormData: formData, tempId: activeId },
      {
        onSuccess: refetch,
        onError: (err: ApiErrorLike) =>
          toast.error(errorKeyFormate(err?.response?.data?.message || err.message || ""), errorConfig),
      },
    );
  };

  const handleAddLinkAttachment = async (link: { name: string; url: string }) => {
    const activeId = await ensureTaskCreated();
    if (!activeId) return;
    addLinkMutation(link, { onSuccess: refetch });
  };

  const handleOpenSharePointPicker = () => {
    if (typeof window === "undefined" || !(window as unknown as { OneDrive?: { open: (opts: unknown) => void } }).OneDrive) {
      toast.error(t("onedrive_script_not_loaded") || "SharePoint / OneDrive picker is not ready.", errorConfig);
      return;
    }
    const odOptions = {
      clientId: msalClientId,
      action: "download",
      multiSelect: true,
      oauth2AllowIdTokenImplicitFlow: true,
      advanced: {
        redirectUri: appUrl,
        openInNewWindow: true,
        queryParameters: "select=id,name,size,file,webUrl",
      },
      cache: {
        cacheLocation: "localStorage",
      },
      success: async function (files: { value?: { name: string; webUrl: string }[] }) {
        if (!files?.value || files.value.length === 0) return;
        const activeId = await ensureTaskCreated();
        if (!activeId) return;
        const payload = {
          file: files.value.map((f) => ({
            name: f.name,
            url: f.webUrl,
          })),
          tempId: activeId,
        };
        sharePointAttechmentAddMutation.mutate(payload, {
          onSuccess() {
            refetch();
          },
          onError(err: ApiErrorLike) {
            toast.error(
              errorKeyFormate(err?.response?.data?.message || err.message || ""),
              errorConfig,
            );
          },
        });
      },
      cancel: function () {
        console.log("User cancelled the SharePoint picker.");
      },
      error: function (e: unknown) {
        console.error("SharePoint picker error:", e);
      },
    };
    (window as unknown as { OneDrive: { open: (opts: unknown) => void } }).OneDrive.open(odOptions);
  };

  const handleOpenGoogleDrivePicker = () => {
    const googleWin = window as unknown as {
      google?: {
        accounts?: {
          oauth2?: {
            initTokenClient: (opts: {
              client_id: string;
              scope: string;
              callback: (res: { access_token?: string }) => void;
            }) => { requestAccessToken: () => void };
          };
        };
        picker?: {
          PickerBuilder: new () => {
            addView: (view: unknown) => {
              setOAuthToken: (token: string) => {
                setDeveloperKey: (key: string) => {
                  setCallback: (cb: (data: { action: string; docs?: { name: string; url: string }[] }) => void) => {
                    build: () => { setVisible: (visible: boolean) => void };
                  };
                };
              };
            };
          };
          ViewId?: { DOCS: unknown };
        };
      };
      gapi?: { load: (api: string, cb: () => void) => void };
    };

    if (!googleWin.google?.accounts?.oauth2) {
      toast.error(t("google_script_not_loaded") || "Google Drive service is not ready.", errorConfig);
      return;
    }

    const handlePickerCallback = async (data: { action: string; docs?: { name: string; url: string }[] }) => {
      if (data.action === "picked" && data.docs && data.docs.length > 0) {
        const activeId = await ensureTaskCreated();
        if (!activeId) return;
        const files = data.docs.map((doc) => ({
          name: doc.name,
          url: doc.url,
        }));
        sharePointAttechmentAddMutation.mutate(
          { file: files, tempId: activeId },
          {
            onSuccess() {
              refetch();
            },
            onError(err: ApiErrorLike) {
              toast.error(
                errorKeyFormate(err?.response?.data?.message || err.message || ""),
                errorConfig,
              );
            },
          },
        );
      }
    };

    const initPicker = (token: string) => {
      if (!googleWin.google?.picker) return;
      const picker = new googleWin.google.picker.PickerBuilder()
        .addView(googleWin.google.picker.ViewId?.DOCS)
        .setOAuthToken(token)
        .setDeveloperKey(googleApiKey)
        .setCallback(handlePickerCallback)
        .build();
      picker.setVisible(true);
    };

    const tokenClient = googleWin.google.accounts.oauth2.initTokenClient({
      client_id: googleCredentialsClientId,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: async (response: { access_token?: string }) => {
        if (response.access_token) {
          googleWin.gapi?.load("picker", () =>
            initPicker(response.access_token!),
          );
        }
      },
    });

    tokenClient.requestAccessToken();
  };

  const handleDeleteTask = () => {
    if (!deleteTaskId) return;
    const parentIdToNavigate = task?.parentTaskId;
    taskRemoveMutation.mutate(deleteTaskId, {
      onSuccess: () => {
        setDeleteTaskId(null);
        if (parentIdToNavigate) {
          // If this was a subtask with a parent task, navigate back to parent task's details
          setTaskId(parentIdToNavigate);
          refetch();
          allTasksQuery.refetch();
        } else {
          props.close("deleted");
        }
      },
      onError: (err: ApiErrorLike) =>
        toast.error(errorKeyFormate(err?.response?.data?.message || err.message || ""), errorConfig),
    });
  };

  const parentTaskName = useMemo(() => {
    const pId = task?.parentTaskId ?? props.createSubtask;
    if (!pId) return undefined;
    return allTasksQuery.data?.data?.data?.find((t) => t.taskId === pId)?.taskName;
  }, [task?.parentTaskId, props.createSubtask, allTasksQuery.data?.data?.data]);

  const assignedMembersList = useMemo(() => {
    return (task?.assignedUsers || [])
      .map((u) => u?.user)
      .filter((u): u is NonNullable<typeof u> => Boolean(u && u.userId));
  }, [task?.assignedUsers]);

  const totalWorkHours = useMemo(() => {
    if (!task?.assignedUsers?.length) return 0;
    const sum = task.assignedUsers.reduce((acc, curr) => acc + (Number(curr?.workingHours) || 0), 0);
    return Math.round(sum * 100) / 100;
  }, [task?.assignedUsers]);

  const allOrgMembersList = useMemo(() => {
    return (projectDetailQuery.data?.data?.data?.assignedUsers || [])
      .filter((u: AssignedUsers) => Boolean(u?.user?.userId))
      .map((u: AssignedUsers) => ({
        userOrganisationId: u.projectAssignUsersId || u.user?.userId || "",
        user: {
          userId: u.user.userId,
          email: u.user.email,
          firstName: u.user.firstName || undefined,
          lastName: u.user.lastName || undefined,
          avatarImg: u.user.avatarImg || undefined,
          isVerified: u.user.isVerified,
          status: u.user.status,
          countryCode: "",
          phoneNumber: "",
          createdByUserId: "",
          isActive: true,
          userAvailability: (u.user?.userAvailability || []) as UserOrganisationType["user"]["userAvailability"],
        },
        role: u.projectRole as UserOrganisationType["role"],
      })) as UserOrganisationType[];
  }, [projectDetailQuery.data?.data?.data?.assignedUsers]);

  const hasSubtasks = Boolean(task?.subtasks && task.subtasks.length > 0);

  // Left Content: Task Information & Modules
  const leftWorkspace = (
    <div className="flex flex-col gap-4 p-5 pb-16 min-h-full">
      {/* 1. Header: Parent breadcrumb, Milestone, Title, Close/Delete */}
      <TaskHeader
        taskName={taskName}
        isEditingName={isEditingName}
        setIsEditingName={setIsEditingName}
        onTaskNameChange={(e) => setTaskName(e.target.value)}
        onTaskNameSubmit={handleTaskNameSubmit}
        isMilestone={isMilestone}
        onMilestoneToggle={handleMilestoneToggle}
        milestoneDisabled={hasSubtasks}
        parentTaskName={parentTaskName}
        onParentTaskClick={task?.parentTaskId ? () => setTaskId(task.parentTaskId as string) : undefined}
        allowed={allowed}
        onClose={() => props.close("")}
        onDeleteRequest={() => taskId && setDeleteTaskId(taskId)}
        canDelete={Boolean(taskId)}
      />

      {/* 2. Status & Progress Scrubbers */}
      <TaskStatusAndProgress
        status={task?.status}
        onStatusChange={handleStatusChange}
        availableStatuses={[
          TaskStatusEnumValue.NOT_STARTED,
          TaskStatusEnumValue.IN_PROGRESS,
          TaskStatusEnumValue.COMPLETED,
        ]}
        isMilestone={isMilestone}
        actualProgress={Number(task?.completionPecentage || 0)}
        expectedProgress={Number(task?.plannedProgression || 0)}
        onProgressCommit={handleProgressCommit}
        hasSubtasks={hasSubtasks}
        allowed={allowed}
        flag={task?.flag}
        startDate={startDate || task?.startDate}
      />

      {/* 3. Date & Duration Matrix */}
      <TaskPropertiesGrid
        startDate={startDate}
        endDate={task?.endDate ? new Date(task.endDate) : undefined}
        duration={duration}
        onStartDateChange={handleStartDateChange}
        onDurationChange={handleDurationChange}
        onEndDateChange={handleEndDateChange}
        hasSubtasks={hasSubtasks}
        isMilestone={isMilestone}
        allowed={allowed}
        disabledDates={{
          nonWorkingDays: orgNonWorkingDays,
          holidays: orgHolidays,
        }}
      />

      {/* 4. Assignee Management */}
      <TaskAssigneeSection
        projectId={props.projectId}
        task={
          task
            ? {
              ...task,
              startDate: new Date(startDate || task.startDate),
              duration: duration || task.duration,
              endDate: task.endDate ? new Date(task.endDate) : (task?.endDate as unknown as Date),
            }
            : undefined
        }
        assignedMembers={assignedMembersList}
        allOrgMembers={allOrgMembersList}
        onToggleMember={handleToggleMember}
        onUpdateMemberAssignment={handleUpdateMemberAssignment}
        allowed={allowed}
        totalWorkHours={totalWorkHours}
        scrollContainerRef={scrollContainerRef}
      />

      {/* 5. Description Area */}
      <TaskDescriptionEditor
        description={description}
        onChange={setDescription}
        onSubmit={handleDescriptionSubmit}
        allowed={allowed}
      />

      {/* 6. Modular Tab Navigation (Subtasks, Attachments, Notes, Dependencies) */}
      <div className="flex flex-col gap-3 mt-1">
        {/* Navigation Tabs Bar */}
        <div className="flex items-center gap-1 border-b border-slate-200/90 pb-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveSubTab("subtasks")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "subtasks"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <ListTree className="w-3.5 h-3.5" />
            <span>{t("sub_tasks")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "subtasks"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.subtasks?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab("attachments")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "attachments"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <Paperclip className="w-3.5 h-3.5" />
            <span>{t("attachment")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "attachments"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.documentAttachments?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab("notes")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "notes"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>{t("notes")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "notes"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.taskNotes?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab("dependencies")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "dependencies"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <Link2 className="w-3.5 h-3.5" />
            <span>{t("dependencies")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "dependencies"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.dependencies?.filter(
                (d) => !d.dependentOnTask?.deletedAt && !d.dependentOnTask?.taskName?.includes("_deleted_")
              )?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab("activity")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "activity"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>{t("activity")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "activity"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.comments?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab("history")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeSubTab === "history"
                ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100/70"
            }`}
          >
            <History className="w-3.5 h-3.5" />
            <span>{t("history")}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeSubTab === "history"
                  ? "bg-blue-200/80 text-blue-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {task?.histories?.length || 0}
            </span>
          </button>
        </div>

        {/* Tab Content Panes */}
        <div>
          {activeSubTab === "subtasks" && (
            <TaskSubtasksList
              subtasks={task?.subtasks}
              allTasks={allTasksQuery.data?.data?.data ?? []}
              currentTaskId={task?.taskId || taskId}
              onAddSubtask={handleAddSubtask}
              onAttachExistingTask={handleAttachExistingTask}
              onSelectSubtask={(id) => setTaskId(id)}
              onDeleteSubtask={(id) => taskRemoveMutation.mutate(id, { onSuccess: refetch })}
              allowed={allowed}
            />
          )}

          {activeSubTab === "attachments" && (
            <TaskAttachmentsSection
              task={task}
              refetch={refetch}
              allowed={allowed}
              onUploadComputerFiles={handleUploadFiles}
              onOpenDropboxPicker={handleOpenSharePointPicker}
              onOpenGoogleDrivePicker={handleOpenGoogleDrivePicker}
              onAddLinkAttachment={handleAddLinkAttachment}
              isUploading={attachmentAddMutation.isPending || sharePointAttechmentAddMutation.isPending}
              isLinkPending={isLinkUploading}
            />
          )}

          {activeSubTab === "notes" && (
            <TaskNotesSection
              notes={task?.taskNotes as unknown as TaskNoteItem[]}
              onAddNote={handleAddNote}
              onUpdateNote={handleUpdateNote}
              onDeleteNote={handleDeleteNote}
              allowed={allowed}
            />
          )}

          {activeSubTab === "dependencies" && (
            <TaskDependenciesSection
              projectId={props.projectId}
              task={task}
              refetch={refetch}
              allowed={allowed}
              setTaskId={setTaskId}
              setTask={setTask}
              allTask={allTasksQuery.data?.data?.data ?? []}
            />
          )}

          {activeSubTab === "activity" && (
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
              <TaskComment
                task={task}
                refetch={refetch}
                allowed={allowed}
                setTaskId={setTaskId}
                setTask={setTask}
                projectId={props.projectId}
              />
            </div>
          )}

          {activeSubTab === "history" && (
            <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
              <TaskHistory task={task} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      isOpen
      onClose={() => props.close("")}
      modalClass="w-[95vw] max-w-6xl h-[92vh] max-h-[92vh] my-auto bg-white rounded-2xl shadow-2xl p-0 !overflow-hidden flex flex-col"
    >
      {(taskQuery.isFetching || taskCreateMutation.isPending) && (
        <Loader className="absolute top-0 left-0 z-50" />
      )}

      {/* Main Single-Pane Workspace */}
      <div ref={scrollContainerRef} className="flex-1 w-full h-full min-h-0 overflow-y-auto">
        {leftWorkspace}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={Boolean(deleteTaskId)}
        onClose={() => setDeleteTaskId(null)}
        modalClass="rounded-xl max-w-sm p-5"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <img src={TrashCan} className="w-12 h-12" alt="Trash" />
          <h3 className="font-bold text-slate-800 text-sm">
            {t("delete_task_confirm_msg")}
          </h3>
          <div className="flex items-center gap-2 mt-2 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTaskId(null)}
              className="flex-1 text-xs"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteTask}
              isLoading={taskRemoveMutation.isPending}
              className="flex-1 text-xs"
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Dependency Conflict Prompt Dialog */}
      {invalidDateConflict && (
        <Dialog
          isOpen={invalidDateConflict}
          onClose={() => {
            setInvalidDateConflict(false);
            setPendingDateUpdate(null);
          }}
          modalClass="rounded-xl max-w-sm p-5"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <img src={TrashCan} className="w-12 h-12" alt="Trash" />
            <p className="text-xs text-slate-700 leading-relaxed font-medium">
              {conflictErrorMessage}
            </p>
            <div className="flex gap-2 w-full mt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setInvalidDateConflict(false);
                  setPendingDateUpdate(null);
                }}
                disabled={removeTaskDependenciesMutation.isPending}
                className="flex-1 text-xs"
              >
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={deletePredecessorDependency}
                isLoading={removeTaskDependenciesMutation.isPending}
                className="flex-1 text-xs bg-red-600 hover:bg-red-700 text-white"
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Project Status Guard Dialog */}
      <ProjectStatusEditBlockedDialog
        isOpen={isProjectEditBlockedDialogOpen}
        onClose={() => {
          setIsProjectEditBlockedDialogOpen(false);
          props.close("");
        }}
        projectStatus={projectStatus}
        projectSettingsLink={`/project-details/${props.projectId}`}
        modalClass="rounded-lg w-3/5 text-center"
      />
    </Dialog>
  );
};

export default memo(TaskDetailModal);
